import { Suspense, useEffect } from 'react'
import { Outlet, useNavigate, useSearchParams } from 'react-router'
import { EOB_CLASSIFICATIONS, isNonFinancialEob, useSubcategories } from '~/entities/categories'
import {
  getConsignmentsUrlWithCurrentParams,
  getConsignmentUrl,
  useCurrentConsignment,
} from '~/entities/consignment'
import {
  getDocumentStepUrl,
  isTaggingClassification,
  useDocument,
  type UIDocumentStep,
} from '~/entities/document'
import { USIndicator } from '~/entities/reviewer'
import { AuditTrailButton, useAuditTrailQueryParams } from '~/features/audit-trail'
import { CheckConsignmentAssigneeDialog } from '~/features/check-consignment-assignee'
import { FORCE_DISABLE_AUDIT_TRAIL } from '~/pages/consignment-clustering/@x/consignment-page'
import { useIsFeatureEnabled } from '~/shared/lib/feature-flags'
import { ResourceLayout, ResourceLoadingSkeleton, Text } from '~/shared/ui'
import { LoadingSkeleton } from './loading-skeleton'

const QUERY_PARAMS_TO_OMIT = ['navigated_from_id', 'navigated_from_step']

type StepperStep = {
  label: string
  route?: string
  accessible?: boolean
  finished?: boolean
}

type StepUrlOptions = {
  queryParams: Record<string, string>
}

function buildStandardStepperSteps(
  navigatedFromId: string,
  navigatedFromStep: UIDocumentStep,
  isTaggingEnabled: boolean,
  options: StepUrlOptions,
): StepperStep[] {
  const buildRoute = (step: Parameters<typeof getDocumentStepUrl>[1]) =>
    getDocumentStepUrl(navigatedFromId, step, {
      queryParamsToOmit: QUERY_PARAMS_TO_OMIT,
      queryParams: options.queryParams,
    })

  const steps: StepperStep[] = [
    {
      label: 'Classification',
      route: buildRoute('classification'),
      accessible: ['classification', 'data', 'carc_rarc', 'patient', 'finished'].includes(
        navigatedFromStep,
      ),
      finished: ['data', 'carc_rarc', 'patient', 'finished'].includes(navigatedFromStep),
    },
    {
      label: 'Data Extraction',
      route: buildRoute('data'),
      accessible: ['data', 'carc_rarc', 'patient', 'finished'].includes(navigatedFromStep),
      finished: ['carc_rarc', 'patient', 'finished'].includes(navigatedFromStep),
    },
  ]

  if (isTaggingEnabled) {
    steps.push({
      label: 'Tagging',
      route: buildRoute('carc_rarc'),
      accessible: ['carc_rarc', 'patient', 'finished'].includes(navigatedFromStep),
      finished: ['patient', 'finished'].includes(navigatedFromStep),
    })
  }

  steps.push({
    label: 'Matching',
    route: buildRoute('patient'),
    accessible: ['patient', 'finished'].includes(navigatedFromStep),
    finished: navigatedFromStep === 'finished',
  })

  return steps
}

function buildEobStepperSteps(
  navigatedFromId: string,
  navigatedFromStep: UIDocumentStep,
  isNonFinancial: boolean,
  options: StepUrlOptions,
): StepperStep[] {
  const buildRoute = (step: Parameters<typeof getDocumentStepUrl>[1]) =>
    getDocumentStepUrl(navigatedFromId, step, {
      queryParamsToOmit: QUERY_PARAMS_TO_OMIT,
      queryParams: options.queryParams,
    })

  const classificationStep: StepperStep = {
    label: 'Classification',
    route: buildRoute('classification'),
    accessible: [
      'classification',
      'eob_payment_deposit',
      'eob_duplication',
      'eob_processing',
      'finished',
    ].includes(navigatedFromStep),
    finished: ['eob_payment_deposit', 'eob_duplication', 'eob_processing', 'finished'].includes(
      navigatedFromStep,
    ),
  }

  if (isNonFinancial) {
    return [
      classificationStep,
      {
        label: 'Duplication',
        route: buildRoute('eob_duplication'),
        accessible: ['eob_duplication', 'eob_processing', 'finished'].includes(navigatedFromStep),
        finished: ['eob_processing', 'finished'].includes(navigatedFromStep),
      },
      {
        label: 'EOB Processing',
        route: buildRoute('eob_processing'),
        accessible: ['eob_processing', 'finished'].includes(navigatedFromStep),
        finished: navigatedFromStep === 'finished',
      },
    ]
  }

  return [
    classificationStep,
    {
      label: 'Payment Deposit',
      route: buildRoute('eob_payment_deposit'),
      accessible: ['eob_payment_deposit', 'eob_duplication', 'eob_processing', 'finished'].includes(
        navigatedFromStep,
      ),
      finished: ['eob_duplication', 'eob_processing', 'finished'].includes(navigatedFromStep),
    },
    {
      label: 'Duplication',
      route: buildRoute('eob_duplication'),
      accessible: ['eob_duplication', 'eob_processing', 'finished'].includes(navigatedFromStep),
      finished: ['eob_processing', 'finished'].includes(navigatedFromStep),
    },
    {
      label: 'EOB Processing',
      route: buildRoute('eob_processing'),
      accessible: ['eob_processing', 'finished'].includes(navigatedFromStep),
      finished: navigatedFromStep === 'finished',
    },
  ]
}

function NavigatedFromSteps({
  navigatedFromId,
  navigatedFromStep,
  consignmentKey,
  consignmentOnshoreOnly,
  consignmentLabel,
}: {
  navigatedFromId: string
  navigatedFromStep: UIDocumentStep
  consignmentKey: string
  consignmentOnshoreOnly: boolean
  consignmentLabel: string
}) {
  const queryParams = useAuditTrailQueryParams()
  const {
    data: { document: navigatedFromDocument },
  } = useDocument({ documentKey: navigatedFromId })
  const subcategories = useSubcategories()
  const isEobEnabled = useIsFeatureEnabled('2026-02-eob-phase-1')

  const isEobClassification =
    isEobEnabled &&
    EOB_CLASSIFICATIONS.includes(
      navigatedFromDocument.category as (typeof EOB_CLASSIFICATIONS)[number],
    )
  const isTaggingEnabled = isTaggingClassification(navigatedFromDocument.category, subcategories)

  const followupSteps: StepperStep[] = isEobClassification
    ? buildEobStepperSteps(
        navigatedFromId,
        navigatedFromStep,
        isNonFinancialEob(navigatedFromDocument.category),
        { queryParams },
      )
    : buildStandardStepperSteps(navigatedFromId, navigatedFromStep, isTaggingEnabled, {
        queryParams,
      })

  const steps: StepperStep[] = [
    {
      label: 'Clustering',
      route: getConsignmentUrl(consignmentKey, { queryParams }),
      accessible: true,
      finished: navigatedFromStep !== 'clustering',
    },
    ...followupSteps,
  ]

  return (
    <ResourceLayout
      steps={steps}
      activeStepIndex={0}
      backUrl={getConsignmentsUrlWithCurrentParams()}
      actions={FORCE_DISABLE_AUDIT_TRAIL ? undefined : <AuditTrailButton />}
      assetCard={
        <div className="flex items-center gap-0.5">
          {consignmentOnshoreOnly && <USIndicator />}
          <div className="px-2">
            <Text
              appearance="BODY2"
              className="uppercase"
              copyable
            >
              {consignmentLabel}
            </Text>
          </div>
        </div>
      }
    >
      <Suspense fallback={<LoadingSkeleton />}>
        <Outlet />
      </Suspense>
    </ResourceLayout>
  )
}

function ConsignmentPageLayout() {
  const { data } = useCurrentConsignment()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const navigatedFromId = searchParams.get('navigated_from_id')
  const navigatedFromStep = searchParams.get('navigated_from_step') as UIDocumentStep | null
  const queryParams = useAuditTrailQueryParams()

  useEffect(() => {
    if (location.pathname !== `/consignments/${data.consignment.key}`) {
      return
    }
    navigate('clustering', { replace: true })
  }, [data.consignment.key, navigate])

  if (navigatedFromId && navigatedFromStep) {
    return (
      <NavigatedFromSteps
        navigatedFromId={navigatedFromId}
        navigatedFromStep={navigatedFromStep}
        consignmentKey={data.consignment.key}
        consignmentOnshoreOnly={!!data.consignment.onshore_only}
        consignmentLabel={data.consignment.key}
      />
    )
  }

  return (
    <ResourceLayout
      steps={[
        {
          label: 'Clustering',
          route: getConsignmentUrl(data.consignment.key, { queryParams }),
        },
      ]}
      activeStepIndex={0}
      backUrl={getConsignmentsUrlWithCurrentParams()}
      actions={FORCE_DISABLE_AUDIT_TRAIL ? undefined : <AuditTrailButton />}
      assetCard={
        <div className="flex items-center gap-0.5">
          {data.consignment.onshore_only && <USIndicator />}
          <div className="px-2">
            <Text
              appearance="BODY2"
              className="uppercase"
              copyable
            >
              {data.consignment.key}
            </Text>
          </div>
        </div>
      }
    >
      <Suspense fallback={<LoadingSkeleton />}>
        <Outlet />
      </Suspense>
    </ResourceLayout>
  )
}

export function ConsignmentPage() {
  return (
    <Suspense
      fallback={
        <ResourceLoadingSkeleton>
          <LoadingSkeleton />
        </ResourceLoadingSkeleton>
      }
    >
      <CheckConsignmentAssigneeDialog />
      <ConsignmentPageLayout />
    </Suspense>
  )
}
